import { Injectable } from '@angular/core';
import { ApiServiceService } from './api-service.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccountSignature, SignaturePlans } from '../pages/signature/signature.interface';
import { LocalStorageUtil } from '../util/local-storage-util';
import { DigitalManagerProducts } from '../pages/signature/digital-manager.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IDatatableOutputDTO } from '../shared/datatables/datatables';
import { HttpParamsMapper } from '../shared/mappers/http-params.mapper';

type Prices = {
  id: number;
  price: number;
};

type Consultations = {
  id: number;
  name: string;
  price: number;
};

export type PlansDTO = {
  id: number;
  key: string;
  name: string;
  indicationLimit: number;
  indicationLimitString?: string;
  userLimit: number;
  isSupportPriority: boolean;
  isSupportPriorityString?: string;
  isExclusive: boolean;
  consultations: Consultations[];
  prices: Prices[];
  digitalManagerOffer: {
    link: string;
  };
};

export type SubscriptionDTO = {
  planId: number;
  status: string;
  startedAt: string;
  finishedAt: string;
  digitalManagerPlanKey: string;
  plan: PlansDTO;
};

@Injectable({
  providedIn: 'root',
})
export class SignatureService {
  constructor(private apiService: ApiServiceService, private http: HttpClient) {}

  accountSignatureInfo: AccountSignature = null;
  url = environment.api_v2;

  getAccountSignature(): Observable<{
    data: AccountSignature;
  }> {
    try {
      return this.apiService.get(`/subscription`).pipe(
        map((signature: any) => {
          if (signature) {
            this.accountSignatureInfo = {
              active: signature.data?.active,
              suspended: signature.data?.suspended,
              identifier: signature.data?.identifier,
              digitalManagerData: signature.data?.digitalManagerData,
              planName: signature.data?.planName,
            };

            LocalStorageUtil.setSignature({
              active: signature.data?.active,
              suspended: signature.data?.suspended,
              identifier: signature.data?.identifier,
              digitalManagerData: signature.data?.digitalManagerData,
              planName: signature.data?.planName,
            });

            return signature;
          } else return null;
        })
      );
    } catch (error) {
      return;
    }
  }

  getUserSubscriptionV2(): Observable<SubscriptionDTO | null> {
    return this.http.get<SubscriptionDTO | null>(`${this.url}/v2/signatures/user-subscription`);
  }

  getPlansV2(): Observable<PlansDTO[]> {
    return this.http.get<PlansDTO[]>(`${this.url}/v2/plan-public/conditions`);
  }

  getActionLinksV2(): Observable<{
    renewUrl: string;
    salesUrl: string;
    canRenew: boolean;
    status: string;
  }> {
    return this.http.get<{
      renewUrl: string;
      salesUrl: string;
      canRenew: boolean;
      status: string;
    }>(`${this.url}/v2/signatures/action-links`);
  }

  getAdministrativeAccountPlanLinkV2(
    accountId: number,
    planId: number
  ): Observable<{ renewUrl: string }> {
    const params = HttpParamsMapper.toHttpParams({ accountId, planId });
    return this.http.get<{ renewUrl: string }>(
      `${this.url}/v2/administrative/signatures/plan-link`,
      { params }
    );
  }

  getPlans(): Promise<DigitalManagerProducts> {
    return this.apiService.get(`/subscription/plans`).toPromise();
  }

  getAccountQuantity(planName = 'Plano Básico'): Observable<any> {
    return this.apiService.post(`/subscription/users-quantity?planName=${planName}`, {
      planName,
    });
  }

  createSignature(plan: SignaturePlans, cardToken = null, installments = 1) {
    return this.apiService.post('/subscription/create', {
      planIdentifier: plan.identifier,
      maxUsers: plan.maxUsers,
      cardToken,
      installments,
    });
  }

  addUsersToSignature(data: any): Observable<any> {
    return this.apiService.post(`/subscription/add-users`, data);
  }

  getAccountInvoices(data: any): Observable<any> {
    return this.apiService.post(`/subscription/invoices`, data);
  }

  cancelSignature(reason: { option: string; observation }): Observable<any> {
    const signature = LocalStorageUtil.getSignature();

    if (!signature.digitalManagerData.subscription_code) return;

    return this.apiService.post(`/subscription/cancel`, {
      subscriptionCode: signature.digitalManagerData.subscription_code,
      reason,
    });
  }

  getAdministrativeAccountsWithPlanV2({
    filter,
    page,
    perPage,
  }: {
    filter?: { status?: string; accountName?: string };
    page: number;
    perPage: number;
  }) {
    const params = HttpParamsMapper.toHttpParams({
      filter: JSON.stringify(filter || {}),
      page,
      perPage,
    });

    return this.http.get<
      IDatatableOutputDTO<{
        accountId: number;
        accountName: string;
        planName: string;
        status: string;
        startedAt: string | null;
        finishedAt: string | null;
        digitalManagerContactId: string;
      }>
    >(`${this.url}/v2/administrative/accounts/signatures`, { params });
  }

  // Atendido pelo backend Django (mesmo host da API; o Kong roteia /digital-manager para ele).
  ensureUserSubscriptionV2() {
    return this.http.get(`${this.url}/digital-manager/user-subscription/ensure`);
  }

  getLoginRedirectTargetV2() {
    return this.http.get<{ destination: 'v2/dashboard' | 'signature' }>(
      `${this.url}/v2/signatures/login-redirect-target`
    );
  }

  getOfferPlansV2(): Observable<PlansDTO[]> {
    return this.http.get<PlansDTO[]>(`${this.url}/v2/signatures/offer-plans`);
  }
}
